export default class Recordset {
  constructor(type, data = []) {
    if (typeof type !== 'string') {
      throw new Error('type must be a string!');
    }
    this._type = type;
    data = data || [];

    if (typeof data !== 'object') {
      throw new Error('data must be an object or an array of objects!');
    }
    if (!Array.isArray(data)) {
      data = [data];
    }
    var flatten = function (should_be_object) {
      while (Array.isArray(should_be_object)) {
        if (should_be_object.length) {
          should_be_object = should_be_object[0];
        } else {
          return false;
        }
      }
      return Object.assign({}, should_be_object);
    };
    this._data = data.map(flatten).filter(f => f);
  }

  get list() {//Vue does not support iterators :(
    return this.private_data.map(item => new Recordset(this._type, [item]));
  }
  get length() {
    return this.private_data.length;
  }
  get data() {
    return this.private_data;
  }
  get keys() {
    if (this.length) {
      return Object.keys(this.private_data[0]);
    } else {
      return [];
    }
  }
  pluck (index = 0) {
    var subdata = this.private_data[index];
    if (typeof subdata === 'undefined') {
      subdata = [];
    }
    return new Recordset(this._type, subdata);
  }
  slice (begin, end) {
    return new Recordset(this._type, this.private_data.slice(begin, end));
  }

  clone () {
    return new Recordset(this._type, JSON.parse(JSON.stringify(this.private_data)));
  }

  tree (parent_id_key = 'parent_id', children_key = null) {
    if (children_key === null) {
      children_key = this._type;
    }
    var all = this.data;
    var build = (parent_id, parent = null) => {
      parent_id = Number(parent_id);
      var list = [];
      for (let item of all) {
        if (Number(item[parent_id_key]) === parent_id) {
          item.get_parent = () => parent;
          item[children_key] = build(item.id, item);
          list.push(item);
        }
      }
      return list;
    }
    return new Recordset(this._type, build(0));
  }

  set (key, value) {
    var data = this.private_data;
    if (typeof value === 'object' && value instanceof Recordset) {
      value = value.clone().data;
    }
    for (let i in data) {
      data[i][key] = value;
    }
    this._data = data;
    return this;
  }

  flat (children_key = null) {
    if (children_key === null) {
      children_key = this._type;
    }
    var all = new Recordset(this._type, []);
    var add = function (items) {
      all.union(items);
      for (let item of items) {
        add(item[children_key]);
      }
    };
    add(this);
    return all;
  }

  set _data(data) {
    this.private_data = data;
    if (data.length) {
      let first_object = data[0];
      for (let [key, value] of Object.entries(first_object)) {
        if (['data', 'list', 'length', 'pluck', 'keys', 'clone', 'union'].includes(key)) {
          key = key + '_';
        }
        if (typeof value === 'object' && value !== null && !(value instanceof Recordset)) {
          Object.defineProperty(this, key, {
            configurable: true,
            get () {
              return new Recordset(key, value);
            },
          });
        } else {
          Object.defineProperty(this, key, {
            value: value,
            writable: false,
            configurable: true,
          });
        }
      }
    }
  }

  [Symbol.iterator]() {
    var index = -1;
    var data = Object.assign({}, this.private_data);
    var type = this._type;

    return {
      next () {
        ++index;
        var done = !(index in data);
        if (done) {
          return {done,}
        } else {
          return {
            value: new Recordset(type, [data[index]]),
            done,
          }
        }
      }
    };
  }

  union (recordset, replace = false) {
    if (recordset._type != this._type) {
      throw new Error('Recordset types do not match (' + this._type + ', ' + recordset._type + ')');
    }
    var thisids = this.map(rec => rec.id);
    for (let i in recordset.list) {
      let existingIndex = thisids.indexOf(recordset.list[i].id);
      if (recordset.list[i].id && existingIndex > -1) {
        if (replace) {
          let temp = this.data;
          //replace value
          temp.splice(existingIndex, 1, recordset.data[i]);
          this._data = temp;
        }
      } else {
        this._data = this.data.concat(recordset.data[i]);
      }
    }
    return this;
  }

  connect (recordset) {
    var type = recordset._type;
    for (let i in this.private_data) {
      var obj = this.private_data[i];
      this.private_data[i][type] = new Recordset(type, []);
      if (obj.hasOwnProperty(recordset._type + '_id')) {
        this.private_data[i][type] = this.private_data[i][type].union(recordset.filtered(obj[type + '_id']));
      }
      for (let record of recordset.list) {
        if (record.hasOwnProperty(this._type + '_id') && record[this._type + '_id'] == obj.id) {
          this.private_data[i][type] = this.private_data[i][type].union(record);
        }
      }
    }
    this._data = this.private_data;
    return this;
  }

  connectByField (recordset, field) {
    var type = recordset._type;
    for (let i in this.private_data) {
      var obj = this.private_data[i];
      this.private_data[i][type] = new Recordset(type, []);
      if (obj.hasOwnProperty(field)) {
        for (let record of recordset.list) {
          if (record.hasOwnProperty(field) && record[field] == obj[field]) {
            this.private_data[i][type] = this.private_data[i][type].union(record);
          }
        }
      }
    }
    this._data = this.private_data;
    return this;
  }

  filter (fn) {
    return new Recordset(this._type, this.list.filter(fn).map(r => r.data[0]));
  }

  map (fn) {
    return this.list.map(fn);
  }

  mapped (path) {
    var keys = path.trim().split('.').filter(p => p);
    if (!keys.length) {
      return this;
    }
    var key = keys.shift();
    var ret = [];
    for (let record of this) {
      let result = record[key];
      if (result instanceof Recordset) {
        if (!(ret instanceof Recordset)) {
          ret = new Recordset(key, []);
        }
        ret.union(result);
      } else {
        ret.push(result);
      }
    }

    if (Array.isArray(ret)) {
      return ret;
    } else if (ret instanceof Recordset) {
      return ret.mapped(keys.join('.'));
    }
  }

  sort (field, reverse = false) {
    reverse = reverse ? -1 : 1;
    var func = field;
    if (typeof field !== 'function') {
      func = (a, b) => reverse*(a[field] < b[field] ? -1 : +1);
    }
    if (Array.isArray(field)) {
      func = (a, b) => reverse*(field.indexOf(a.id) < field.indexOf(b.id) ? -1 : +1);
    }
    this.private_data.sort(func);
    return this;
  }

  sorted (field, reverse = false) {
    return this.clone().sort(field, reverse);
  }

  filtered () {
    var field, operator, value;
    switch (arguments.length) {
      case 1:
        field = 'id';
        operator = '=';
        value = arguments[0];
        break;
      case 2:
        field = arguments[0];
        operator = '=';
        value = arguments[1];
        break;
      default:
        field = arguments[0];
        operator = arguments[1];
        value = arguments[2];
        break;
    }
    var data = [];
    for (let item of this.data) {
      switch (operator) {
        case '=':
          if (item[field] == value) {
            data[data.length] = item;
          }
          break;
        case '!=':
          if (item[field] != value) {
            data[data.length] = item;
          }
          break;
      }
    }
    return new Recordset(this._type, data);
  }
}


// WEBPACK FOOTER //
// ./src/providers/api/Recordset.js